<script setup lang="ts">
import {
	onBeforeUnmount,
	ref,
	watch
} from 'vue'

import { type Editor, Extension } from '@tiptap/core'
import Placeholder from '@tiptap/extension-placeholder'
import TextAlign from '@tiptap/extension-text-align'
import Underline from '@tiptap/extension-underline'
import { type EditorState, Plugin, type Transaction } from '@tiptap/pm/state'
import StarterKit from '@tiptap/starter-kit'
import { EditorContent, useEditor } from '@tiptap/vue-3'

import type { ICPTextEditorControlItems } from '~/portal/components/base'
import { CPSvgIcon } from '~/portal/components/base'
import { CPTypography } from '~/portal/components/layout'

const props = withDefaults(
	defineProps<{
		modelValue?: string
		placeholder?: string
		disabled?: boolean
		error?: boolean
		errorMessages?: string | string[]
		maxLength?: number
	}>(),
	{
		error: false,
		errorMessages: '',
		placeholder: 'Hinterlassen Sie hier Ihren Kommentar'
	}
)

const charactersCount = ref(0)

const characterLimit = props.maxLength
	? [
		Extension.create({
			name: 'characterLimit',
			addProseMirrorPlugins () {
				return [
					new Plugin({
						filterTransaction: (
							transaction: Transaction,
							state: EditorState
						): boolean => {
							if (!transaction.docChanged) return true

							const newLength = transaction.doc.textContent.length
							const oldLength = state.doc.textContent.length

							// allow edits within the limit; always allow
							// shrinking even if already over the limit
							return (
								newLength <= props.maxLength! ||
									newLength <= oldLength
							)
						}
					})
				]
			}
		})
	]
	: []

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | undefined): void
  (e: 'input', value: string | undefined): void
  (e: 'keyup'): void
}>()

const model = defineModel<string>()

const editor = useEditor({
	extensions: [
		StarterKit,
		Underline,
		TextAlign.configure({
			types: ['heading', 'paragraph']
		}),
		Placeholder.configure({
			placeholder: props.placeholder
		}),
		...characterLimit
	],
	content: props.modelValue,
	onCreate: ({ editor }: { editor: Editor }): void => {
		charactersCount.value = editor.state.doc.textContent.length
	},
	onUpdate: (): void => {
		charactersCount.value = editor.value!.state.doc.textContent.length
		emit('input', editor.value!.getText() ? editor.value!.getHTML() : '')
		emit('update:modelValue', editor.value!.getText() ? editor.value!.getHTML() : '')
	},
	editorProps: {
		handleKeyDown (_, event) {
			if (event.shiftKey && event.key === 'Enter') {
				event.preventDefault()
				emit('keyup')
				return true
			}
			return false
		}
	}
})

const controlItems = ref<ICPTextEditorControlItems>([
	[
		{
			icon: 'bold-symbol',
			style: 'bold',
			size: 10
		},
		{
			icon: 'italic-symbol',
			style: 'italic',
			size: 12
		},
		{
			icon: 'underline-symbol',
			style: 'underline',
			size: 15
		}
	],
	[
		{
			icon: 'align-left-symbol',
			style: 'align-left'
		},
		{
			icon: 'align-center-symbol',
			style: 'align-center'
		},
		{
			icon: 'align-right-symbol',
			style: 'align-right'
		},
		{
			icon: 'align-justify-symbol',
			style: 'align-justify'
		}
	],
	[
		{
			icon: 'list-bullet-symbol',
			style: 'list-bullet'
		},
		{
			icon: 'list-numeric-symbol',
			style: 'list-numeric'
		}
	]
])

const onSetStyle = (style: string): void => {
	switch (style) {
	case 'bold':
		editor.value!.chain().focus().toggleBold().run()
		break

	case 'italic':
		editor.value!.chain().focus().toggleItalic().run()
		break

	case 'underline':
		editor.value!.chain().focus().toggleUnderline().run()
		break

	case 'align-left':
		editor.value!.chain().focus().setTextAlign('left').run()
		break

	case 'align-center':
		editor.value!.chain().focus().setTextAlign('center').run()
		break

	case 'align-right':
		editor.value!.chain().focus().setTextAlign('right').run()
		break

	case 'align-justify':
		editor.value!.chain().focus().setTextAlign('justify').run()
		break

	case 'list-bullet':
		editor.value!.chain().focus().toggleBulletList().run()
		break

	case 'list-numeric':
		editor.value!.chain().focus().toggleOrderedList().run()
		break

	default:
		break
	}
}

watch(model, (value) => {
	const isSame = editor.value!.getHTML() === value

	if (isSame) {
		return
	}

	editor.value!.commands.setContent(value!, false)
	charactersCount.value = editor.value!.state.doc.textContent.length
})

onBeforeUnmount(() => {
	if (editor.value) {
		editor.value.destroy()
	}
})

defineExpose({
	editor
})
</script>

<script lang="ts">
export default { inheritAttrs: false }
</script>

<template>
  <div
    :class="[
      'cp-text-editor',
      { 'cp-text-editor--disabled': disabled },
      { 'cp-text-editor--error': error },
      $attrs.class
    ]"
  >
    <div
      class="cp-text-editor__inner"
    >
      <div
        v-if="editor"
        class="cp-text-editor__control"
      >
        <div
          v-for="(item, index) in controlItems"
          :key="index"
          class="cp-text-editor__control-group"
        >
          <CPSvgIcon
            v-for="icon in item"
            :key="icon.style"
            :name="icon.icon"
            :size="icon?.size ?? 14"
            class="cp-text-editor__control-icon"
            @click="onSetStyle(icon.style)"
          />
        </div>
      </div>
      <EditorContent
        v-model="model"
        :editor="editor"
        class="cp-text-editor__editor"
      />
    </div>
    <CPTypography
      v-if="maxLength"
      variant="caption2"
      class="cp-text-editor__counter"
    >
      {{ charactersCount }}/{{ maxLength }}
    </CPTypography>
    <CPTypography
      v-for="(message, index) in errorMessages"
      :key="index"
      variant="caption2"
      class="cp-text-editor__error"
    >
      {{ message }}
    </CPTypography>
  </div>
</template>

<style lang="scss">
  .cp-text-editor {
    @apply
      min-w-0
      max-w-full
    ;

    &__inner {
      @apply
      bg-cp-neutral-50
      rounded
      border-solid
      border-1
      border-cp-neutral-200
      min-w-0
      max-w-full
      overflow-hidden
      ;
    }

    &__control {
      @apply
        flex
        gap-8
        m-[15px]
        pb-4
				px-[5px]
        border
        border-transparent
        border-solid
        border-b-cp-neutral-200
      ;

      &-group {
        @apply
          flex
          gap-4
        ;
      }

			&-icon {
				@apply
					text-cp-neutral-500
					cursor-pointer
				;
			}
    }

    &__error {
      @apply
        mt-3
        px-4
        color-cp-base-red
      ;
    }

    &__counter {
      @apply
        text-right
        mt-2
        px-4
        color-cp-neutral-500
      ;
    }

    &__editor {
      @apply
				flex
        m-4
				mt-5
				min-h-32
				max-h-100
				min-w-0
				max-w-full
      ;

			.tiptap {
				@apply
					w-full
					min-w-0
					max-w-full
					min-h-full
					overflow-y-auto
					overflow-x-hidden
					text-cp-neutral-700
				;
				word-break: break-word;
				overflow-wrap: anywhere;

				&:focus-visible {
					outline: none
				}

				& > * {
					@apply
						mb-3
						max-w-full
					;
					word-break: break-word;
					overflow-wrap: anywhere;
				}
			}

			ul, ol {
				@apply
					ml-8
				;
			}

      p.is-editor-empty:first-child{
				@apply
					relative
				;

				&::before {
					content: attr(data-placeholder);
					@apply
						absolute
						top-0
						left-0
						text-cp-neutral-500
						pointer-events-none
					;
				}
			}
    }

		&--disabled {
      .cp-text-editor__inner {
        @apply
          opacity-50
          pointer-events-none
        ;
      }
		}
  }
</style>
